import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getBusinessUrl } from '../../utils/urlHelpers';
import { firstImageOrPlaceholder, IMAGE_PLACEHOLDER_PATH } from '../../utils/imageFallback';

const BusinessGrid = ({ businesses, societySlug, onBusinessClick, loading = false }) => {
  const navigate = useNavigate();

  const handleCardClick = (business) => {
    if (onBusinessClick) {
      onBusinessClick(business);
    } else {
      navigate(getBusinessUrl(business, societySlug));
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      shop: 'bg-blue-100 text-blue-800',
      company: 'bg-green-100 text-green-800',
      restaurant: 'bg-red-100 text-red-800',
      cafe: 'bg-orange-100 text-orange-800',
      salon: 'bg-pink-100 text-pink-800',
      clinic: 'bg-purple-100 text-purple-800',
      school: 'bg-indigo-100 text-indigo-800',
      gym: 'bg-cyan-100 text-cyan-800',
      pharmacy: 'bg-yellow-100 text-yellow-800',
      bank: 'bg-emerald-100 text-emerald-800',
      property_dealer: 'bg-amber-100 text-amber-800',
      other: 'bg-slate-100 text-slate-800'
    };
    return colors[category] || colors.other;
  };

  if (loading) {
    return (
      <div className="col-span-full flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!businesses || businesses.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 mb-4">
          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No businesses found</h3>
        <p className="text-gray-500">
          Try adjusting your search criteria or browse all businesses.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3 sm:gap-6">
      {businesses.map((business, index) => (
        <React.Fragment key={business.id}>
          <div 
            className="bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-shadow duration-200 cursor-pointer flex flex-col h-full"
            onClick={() => handleCardClick(business)}
          >
            {/* Business Image */}
            <div className="h-32 sm:h-48 bg-gray-200 flex items-center justify-center flex-shrink-0">
              <img
                src={firstImageOrPlaceholder([
                  business.display_image,
                  ...(Array.isArray(business.images) ? business.images : []),
                ])}
                alt={business.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = IMAGE_PLACEHOLDER_PATH;
                }}
              />
            </div>

            {/* Business Info */}
            <div className="p-4 sm:p-6 flex flex-col flex-grow">
              {/* Content Area - grows to fill available space */}
              <div className="flex-grow">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 line-clamp-1">
                    {business.name}
                  </h3>
                </div>

                <p className="text-gray-600 text-xs sm:text-sm mb-3 line-clamp-2">
                  {business.description}
                </p>

                <div className="flex items-center mb-3">
                  <span className={`px-2 py-1 text-xs rounded-full ${getCategoryColor(business.category)}`}>
                    {business.category?.replace('_', ' ').toUpperCase() || 'OTHER'}
                  </span>
                </div>

                {/* Rating */}
                {business.rating > 0 && (
                  <div className="flex items-center mb-3">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <svg
                          key={i}
                          className={`w-4 h-4 ${
                            i < Math.floor(business.rating)
                              ? 'text-yellow-400'
                              : 'text-gray-300'
                          }`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                      <span className="ml-1 text-sm text-gray-600">
                        {business.rating.toFixed(1)} ({business.total_ratings || 0})
                      </span>
                    </div>
                  </div>
                )}

                {/* Contact Info */}
                {business.contact_phone && (
                  <div className="flex items-center text-sm text-gray-600 mb-2">
                    <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <a
                      href={`tel:${business.contact_phone}`}
                      className="text-blue-600 hover:text-blue-800"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {business.contact_phone}
                    </a>
                  </div>
                )}

                {/* View Count */}
                <div className="flex items-center text-sm text-gray-500">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {business.views || 0} views
                </div>
              </div>

              {/* Action Button - always at bottom */}
              <div className="mt-auto pt-3 border-t border-gray-200">
                <button 
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCardClick(business);
                  }}
                >
                  View Details
                </button>
              </div>
            </div>
          </div>

          {/* Removed in-feed ad to avoid navigation/content confusion per AdSense policy */}
        </React.Fragment>
      ))}
    </div>
  );
};

export default BusinessGrid;
